import * as React from "react";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import * as validator from "validator";
import {none, Option, option} from "ts-option";
import AddressSelectionComponent from "../address/select/AddressSelectionComponent";
import Address from "../../model/address/Address";
import {RootState} from "../../store/reducer/root/RootState";
import SelfServiceSignupApi, {
	selfServiceSignupApi,
	SelfServiceSignupAddressApiDto,
	SelfServiceSignupApiDto
} from "../../api/SelfServiceSignupApi";
import TaxiClaimApi, {
	taxiClaimApi,
	TaxiClaimResponseApiDto,
	TaxiClaimSubmissionApiDto
} from "../../api/TaxiClaimApi";
import TurnstileWidget from "../signup/TurnstileWidget";
import SignaturePad from "./SignaturePad";
import {attachmentFileName, reencodeImageToJpeg} from "./taxiClaimImage";
import {trackTaxiClaimStep} from "./taxiClaimAnalytics";
import {Environment} from "../../model/settings/Settings";
import "./taxiClaimPage.scss";

const logo = "/assets/img/dg-logo-current.svg";
const taxInsuranceExample = require("../../../resources/hiber/img/taxi-claim/tax_insurance_example.jpg");
const dashboardMileageExample = require("../../../resources/hiber/img/taxi-claim/dashboard_mileage_example.png");
const vehicleFrontExample = require("../../../resources/hiber/img/taxi-claim/front_of_vehicle_example.jpg");
const vehicleBackExample = require("../../../resources/hiber/img/taxi-claim/back_of_vehicle_example.jpg");
const vehicleLicenceExample = require("../../../resources/hiber/img/taxi-claim/nta_spsv_licence_disc_example.png");
const contactEmail = "info@drivinggreen.com";
/**
 * Journey this page speaks for. Sent twice: once when the account is created, where it is what tells the
 * backend to register the driver with no cap on their mileage payouts, and again with the claim itself,
 * where it decides who gets the confirmation email. The same word both times on purpose.
 */
const journeySource = "TAXI_WEB";
// React 16's type definitions predate the string-valued HTML capture attribute.
const rearCameraCaptureProps: any = {capture: "environment"};

type JourneyStep =
	"welcome" |
	"account" |
	"taxi-check" |
	"owner-check" |
	"vehicle" |
	"eligible" |
	"photos" |
	"review" |
	"signature" |
	"confirmation" |
	"ineligible" |
	"existing-account";

interface StepDetails {
	label: string;
	progress: number;
}

const stepDetails: {[step: string]: StepDetails} = {
	"welcome": {label: "Welcome", progress: 0},
	"account": {label: "About you", progress: 14},
	"taxi-check": {label: "Vehicle type", progress: 38},
	"owner-check": {label: "Ownership", progress: 46},
	"vehicle": {label: "Vehicle details", progress: 54},
	"eligible": {label: "Eligible to claim", progress: 62},
	"photos": {label: "Add photos", progress: 72},
	"review": {label: "Review claim", progress: 82},
	"signature": {label: "Sign off", progress: 90},
	"confirmation": {label: "Submitted", progress: 100},
	"ineligible": {label: "Not eligible", progress: 100},
	"existing-account": {label: "Account found", progress: 100}
};

type PhotoKey = "taxInsurance" | "dashboardMileage" | "vehicleFront" | "vehicleBack" | "vehicleLicence";
type PhotoStatus = "empty" | "processing" | "uploading" | "done" | "failed";

interface PhotoDefinition {
	key: PhotoKey;
	label: string;
	description: string;
	prefix: string;
	icon: string;
	exampleUrl?: string;
}

const photoDefinitions: PhotoDefinition[] = [
	{
		key: "taxInsurance",
		label: "Tax and insurance discs",
		description: "Clearly photograph your tax and insurance disc in the plastic pockets on the windscreen of your car.",
		prefix: "tax_insurance_",
		icon: "fa-id-card-o",
		exampleUrl: taxInsuranceExample
	},
	{
		key: "dashboardMileage",
		label: "Odometer (mileage reading in KM)",
		description: "Remove your tax disc from the windscreen holder and place in front of the odometer reading on your car's dashboard. Photograph the two side by side.",
		prefix: "dashboard_mileage_",
		icon: "fa-tachometer",
		exampleUrl: dashboardMileageExample
	},
	{
		key: "vehicleFront",
		label: "Front of vehicle with registration",
		description: "Include the full car and registration plate.",
		prefix: "vehicle_front_",
		icon: "fa-car",
		exampleUrl: vehicleFrontExample
	},
	{
		key: "vehicleBack",
		label: "Back of vehicle with registration",
		description: "Include the full car and registration plate.",
		prefix: "vehicle_back_",
		icon: "fa-car",
		exampleUrl: vehicleBackExample
	},
	{
		key: "vehicleLicence",
		label: "NTA SPSV Licence Disc",
		description: "Photograph your windshield licensing disc ensuring NTA SPSV Licence Number is clearly visible",
		prefix: "vehicle_licence_",
		icon: "fa-id-card-o",
		exampleUrl: vehicleLicenceExample
	}
];

interface PhotoUploadState {
	status: PhotoStatus;
	file?: File;
	blob?: Blob;
	previewUrl?: string;
	attachmentId?: string;
	error?: string;
}

interface ReduxProps {
	turnstileSiteKey: string;
	analyticsEnabled: boolean;
	gaMeasurementId: string;
}

interface State {
	step: JourneyStep;
	stepHistory: JourneyStep[];
	firstName: string;
	lastName: string;
	businessName: string;
	email: string;
	phoneNumber: string;
	driverLicenceNumber: string;
	address: Option<Address>;
	manualAddress: boolean;
	manualStreet: string;
	manualCity: string;
	manualEircode: string;
	consent: boolean;
	turnstileToken: string;
	accountLoading: boolean;
	accountErrors: {[field: string]: string};
	accountRequestError: string;
	registration: string;
	mileage: string;
	vehicleErrors: {[field: string]: string};
	previewLoading: boolean;
	previewError: string;
	previewPayoutCents: number;
	ineligibleReason: "vehicle" | "owner" | "claim";
	utilityName: string;
	photoIndex: number;
	photos: {[key: string]: PhotoUploadState};
	uploadStarted: boolean;
	signatureHasStroke: boolean;
	signatureAttachmentId: string;
	submitting: boolean;
	submissionError: string;
	examplePhotoKey: PhotoKey | "";
	claimResult?: TaxiClaimResponseApiDto;
	referenceCopied: boolean;
}

type Props = ReduxProps & RouteComponentProps<{}>;

function emptyPhotos(): {[key: string]: PhotoUploadState} {
	return {
		taxInsurance: {status: "empty"},
		dashboardMileage: {status: "empty"},
		vehicleFront: {status: "empty"},
		vehicleBack: {status: "empty"},
		vehicleLicence: {status: "empty"}
	};
}

export class TaxiClaimPage extends React.Component<Props, State> {
	private signupApi: SelfServiceSignupApi = selfServiceSignupApi;
	private claimApi: TaxiClaimApi = taxiClaimApi;
	private accessToken: string = "";
	private addressSelection: AddressSelectionComponent;
	private turnstileWidget: TurnstileWidget;
	private signaturePad: SignaturePad;
	private screen: HTMLElement;

	// Bound once, not inline in JSX: a fresh inline ref/prop function on every render made
	// SignaturePad (a PureComponent) re-render mid-stroke and churn its own <canvas> ref, which is
	// what broke drawing after the first touch. See SignaturePad.setCanvasRef for the full story.
	private setSignaturePadRef = (component: SignaturePad) => this.signaturePad = component;
	private handleSignatureStrokeChange = (signatureHasStroke: boolean) =>
		this.setState({signatureHasStroke, signatureAttachmentId: ""});
	private exampleDialog: HTMLElement;
	private submitLocked: boolean = false;
	private referenceValue: HTMLElement;

	constructor(props: Props) {
		super(props);
		this.state = {
			step: "welcome",
			referenceCopied: false,
			stepHistory: [],
			firstName: "",
			lastName: "",
			businessName: "",
			email: "",
			phoneNumber: "",
			driverLicenceNumber: "",
			address: none,
			manualAddress: false,
			manualStreet: "",
			manualCity: "",
			manualEircode: "",
			consent: false,
			turnstileToken: "",
			accountLoading: false,
			accountErrors: {},
			accountRequestError: "",
			registration: "",
			mileage: "",
			vehicleErrors: {},
			previewLoading: false,
			previewError: "",
			previewPayoutCents: 0,
			ineligibleReason: "vehicle",
			utilityName: "",
			photoIndex: 0,
			photos: emptyPhotos(),
			uploadStarted: false,
			signatureHasStroke: false,
			signatureAttachmentId: "",
			submitting: false,
			submissionError: "",
			examplePhotoKey: ""
		};
		this.warnBeforeUnload = this.warnBeforeUnload.bind(this);
	}

	componentDidMount() {
		window.addEventListener("beforeunload", this.warnBeforeUnload);
		this.trackStep();
	}

	componentDidUpdate(previousProps: Props, previousState: State) {
		if (previousProps.analyticsEnabled !== this.props.analyticsEnabled ||
			previousState.step !== this.state.step ||
			(this.state.step === "photos" && previousState.photoIndex !== this.state.photoIndex)) {
			this.trackStep();
		}
	}

	private trackStep() {
		if (this.props.analyticsEnabled) {
			trackTaxiClaimStep(this.props.gaMeasurementId, this.state.step,
				this.state.step === "photos" ? this.state.photoIndex + 1 : undefined);
		}
	}

	componentWillUnmount() {
		window.removeEventListener("beforeunload", this.warnBeforeUnload);
		photoDefinitions.forEach(definition => {
			const previewUrl = this.state.photos[definition.key].previewUrl;
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		});
		this.accessToken = "";
	}

	render() {
		const details = stepDetails[this.state.step];
		const progress = this.state.step === "photos"
			? 62 + Math.round(((this.state.photoIndex + 1) / photoDefinitions.length) * 20)
			: details.progress;
		const label = this.state.step === "photos"
			? `Photo ${this.state.photoIndex + 1} of ${photoDefinitions.length}`
			: details.label;
		return (
			<div className="c-taxi-claim">
				<header className="c-taxi-claim__header">
					<div className="c-taxi-claim__nav">
						{this.canGoBack() ? (
							<button type="button" className="c-taxi-claim__back" onClick={() => this.goBack()} aria-label="Go back">
								{this.icon("chevron-left", 24)}
							</button>
						) : <span className="c-taxi-claim__nav-spacer"/>}
						<img className="c-taxi-claim__logo" src={logo} alt="Driving Green"/>
						<span className="c-taxi-claim__nav-label">{label}</span>
					</div>
					<div className="c-taxi-claim__progress" role="progressbar" aria-valuemin={0} aria-valuemax={100}
						 aria-valuenow={progress} aria-label="Claim progress">
						<span style={{width: `${progress}%`}}/>
					</div>
				</header>
				<main className="c-taxi-claim__main">
					<section className="c-taxi-claim__screen" tabIndex={-1} ref={(element) => this.screen = element}>
						{this.renderStep()}
					</section>
				</main>
				{this.renderPhotoExample()}
			</div>
		);
	}

	private renderStep(): React.ReactNode {
		switch (this.state.step) {
			case "welcome": return this.renderWelcome();
			case "account": return this.renderAccount();
			case "taxi-check": return this.renderTaxiCheck();
			case "owner-check": return this.renderOwnerCheck();
			case "vehicle": return this.renderVehicle();
			case "eligible": return this.renderEligible();
			case "photos": return this.renderPhotos();
			case "review": return this.renderReview();
			case "signature": return this.renderSignature();
			case "confirmation": return this.renderConfirmation();
			case "ineligible": return this.renderIneligible();
			case "existing-account": return this.renderExistingAccount();
		}
		return null;
	}

	private renderWelcome() {
		return (
			<React.Fragment>
				{this.kicker("Driving Green")}
				<h1>Get rewarded for driving green</h1>
				<p className="c-taxi-claim__body-copy c-taxi-claim__body-copy--tight">
					Find out what driving electric can earn you
				</p>
				<p className="c-taxi-claim__body-copy">
					Driving Green rewards taxi and private-hire drivers for the mileage they drive in an electric vehicle.
					Create your account, add a few vehicle details and submit your claim in one go.
				</p>
				<div className="c-taxi-claim__grow"/>
				<button type="button" className="c-taxi-claim__primary" onClick={() => this.navigateFresh("account")}>
					Get started {this.icon("arrow-right")}
				</button>
			</React.Fragment>
		);
	}

	private renderAccount() {
		const eircode = this.state.address.map(address => address.postcode || "").getOrElse("");
		return (
			<React.Fragment>
				{this.kicker("About you")}
				<h1 className="c-taxi-claim__small-heading">Tell us a bit about you</h1>
				<p className="c-taxi-claim__body-copy c-taxi-claim__body-copy--tight">Just a few details to get you started.</p>
				<form onSubmit={(event) => this.submitAccount(event)} noValidate={true}>
					<div className="c-taxi-claim__field-row">
						{this.textField("firstName", "First name", "text", "Aoife", 100)}
						{this.textField("lastName", "Last name", "text", "Byrne", 100)}
					</div>
					{this.textField("businessName", "Business name", "text", "e.g. Byrne Private Hire Ltd", 200,
						"(or your name if sole trader)")}
					{this.textField("email", "Email address", "email", "aoife@example.ie", 254)}
					<div className="c-taxi-claim__field c-taxi-claim__address">
						<label>Business Address <span className="c-taxi-claim__label-note">(or home address if sole trader)</span></label>
						{this.state.manualAddress ? (
							// Always the editable fields while in this mode, never the summary below:
							// swapping to a read-only "confirmed" view the moment the city has
							// one character would yank the Eircode input out from under the driver mid-type.
							<div className="c-taxi-claim__manual-address">
								<div className="c-taxi-claim__field">
									<label htmlFor="taxi-claim-manual-street">Street <span className="c-taxi-claim__label-note">(optional)</span></label>
									<input id="taxi-claim-manual-street" value={this.state.manualStreet}
										   placeholder="Dame Street"
										   onChange={(event) => this.updateManualAddress("manualStreet", event.target.value)}/>
								</div>
								<div className="c-taxi-claim__field-row">
									<div className="c-taxi-claim__field">
										<label htmlFor="taxi-claim-manual-city">City / Town</label>
										<input id="taxi-claim-manual-city" value={this.state.manualCity}
											   placeholder="Dublin"
											   onChange={(event) => this.updateManualAddress("manualCity", event.target.value)}/>
									</div>
									<div className="c-taxi-claim__field">
										<label htmlFor="taxi-claim-manual-eircode">Eircode <span className="c-taxi-claim__label-note">(optional)</span></label>
										<input id="taxi-claim-manual-eircode" value={this.state.manualEircode}
											   placeholder="D02 XY45"
											   onChange={(event) => this.updateManualAddress("manualEircode", event.target.value.toUpperCase())}/>
									</div>
								</div>
								<button type="button" className="c-taxi-claim__address-toggle"
										onClick={() => this.toggleManualAddress(false)}>
									Search for the address instead
								</button>
							</div>
						) : this.state.address.map(address => (
							<div className="c-taxi-claim__selected-address">
								<span>{address.displayName()}</span>
								<button type="button" onClick={() => this.clearAddress()}>Change</button>
							</div>
						)).getOrElse(() => (
							<React.Fragment>
								<AddressSelectionComponent
									ref={(component) => this.addressSelection = component}
									addressApi={this.signupApi.addressApi}
									onSelect={(address) => this.selectAddress(address)}
									visible={true}
									placeholder="Start typing an Eircode or address"
									initialSearchQuery={none}/>
								<button type="button" className="c-taxi-claim__address-toggle"
										onClick={() => this.toggleManualAddress(true)}>
									Can't find your address? Enter it manually
								</button>
							</React.Fragment>
						))}
						{this.accountError("address")}
					</div>
					{this.state.manualAddress ? (
						// The manual address block above already has its own editable Eircode field -
						// showing this read-only one too was a plain duplicate, not just a layout wart.
						this.textField("phoneNumber", "Phone number", "tel", "+353 87 123 4567", 32)
					) : (
						<div className="c-taxi-claim__field-row">
							<div className="c-taxi-claim__field">
								<label htmlFor="taxi-claim-eircode">Eircode <span className="c-taxi-claim__label-note">(optional)</span></label>
								<input id="taxi-claim-eircode" value={eircode} readOnly={true} placeholder="From address"/>
							</div>
							{this.textField("phoneNumber", "Phone number", "tel", "+353 87 123 4567", 32)}
						</div>
					)}
					{this.textField("driverLicenceNumber", "NTA SPSV Licence Number", "text", "12347", 100)}
					<div className="c-taxi-claim__turnstile">
						{this.props.turnstileSiteKey ? (
							<TurnstileWidget
								ref={(component) => this.turnstileWidget = component}
								siteKey={this.props.turnstileSiteKey}
								onTokenChange={(turnstileToken) => this.setState({turnstileToken})}
								onError={() => this.setState({accountRequestError: "The security check could not be loaded. Please refresh and try again."})}/>
						) : (
							<div className="c-taxi-claim__notice c-taxi-claim__notice--error" role="alert">
								Registration is not configured for this environment.
							</div>
						)}
						{this.accountError("turnstileToken")}
					</div>
					<label className="c-taxi-claim__checkbox">
						<input type="checkbox" checked={this.state.consent}
							   onChange={(event) => this.setState({consent: event.target.checked})}/>
						<span>I agree to Driving Green contacting me about my account and rewards.</span>
					</label>
					{this.accountError("consent")}
					{this.state.accountRequestError && (
						<div className="c-taxi-claim__notice c-taxi-claim__notice--error" role="alert">
							{this.state.accountRequestError}
						</div>
					)}
					<button type="submit" className="c-taxi-claim__primary"
							disabled={this.state.accountLoading || !this.state.consent || !this.props.turnstileSiteKey}>
						{this.state.accountLoading ? "Creating account…" : "Continue"}
						<span style={this.state.accountLoading ? {display: "none"} : undefined}>
							{this.icon("arrow-right")}
						</span>
					</button>
				</form>
			</React.Fragment>
		);
	}

	private renderTaxiCheck() {
		return this.choiceScreen(
			"Screening",
			"Is this vehicle registered as a taxi or private hire vehicle?",
			"This determines which funding route applies.",
			() => this.navigate("owner-check"),
			() => this.setState({ineligibleReason: "vehicle"}, () => this.finishAt("ineligible"))
		);
	}

	private renderOwnerCheck() {
		return this.choiceScreen(
			"Screening",
			"Are you the registered owner of this vehicle?",
			"We can only process a claim from the vehicle's registered owner.",
			() => this.navigate("vehicle"),
			() => this.setState({ineligibleReason: "owner"}, () => this.finishAt("ineligible"))
		);
	}

	private renderVehicle() {
		return (
			<React.Fragment>
				{this.kicker("Vehicle eligibility")}
				<h1 className="c-taxi-claim__small-heading">Let's check your vehicle eligibility</h1>
				<p className="c-taxi-claim__body-copy c-taxi-claim__body-copy--tight">
					Enter your vehicle registration and current mileage.
				</p>
				<form className="c-taxi-claim__form-grow" onSubmit={(event) => this.submitVehicle(event)} noValidate={true}>
					<div className="c-taxi-claim__field">
						<label htmlFor="taxi-claim-registration">Registration</label>
						<input id="taxi-claim-registration" value={this.state.registration} maxLength={20}
							   className="c-taxi-claim__uppercase" placeholder="252ND5678" disabled={this.state.previewLoading}
							   onChange={(event) => this.updateVehicleField("registration", event.target.value.toUpperCase())}/>
						{this.vehicleError("registration")}
					</div>
					<div className="c-taxi-claim__field">
						<label htmlFor="taxi-claim-mileage">Current mileage (km)</label>
						<input id="taxi-claim-mileage" value={this.state.mileage} inputMode="numeric" placeholder="51,078"
							   disabled={this.state.previewLoading}
							   onChange={(event) => this.updateVehicleField("mileage", event.target.value)}/>
						{this.vehicleError("mileage")}
					</div>
					{this.state.previewError && (
						<div className="c-taxi-claim__notice c-taxi-claim__notice--error" role="alert">
							{this.state.previewError}
						</div>
					)}
					<div className="c-taxi-claim__grow"/>
					<button type="submit" className="c-taxi-claim__primary" disabled={this.state.previewLoading}>
						{this.state.previewLoading ? "Checking eligibility…" : "Check eligibility"}
						<span style={this.state.previewLoading ? {display: "none"} : undefined}>
							{this.icon("arrow-right")}
						</span>
					</button>
				</form>
			</React.Fragment>
		);
	}

	private renderEligible() {
		return (
			<React.Fragment>
				<div className="c-taxi-claim__result c-taxi-claim__result--positive">
					<span className="c-taxi-claim__result-icon">{this.icon("check", 22)}</span>
					<span className="c-taxi-claim__result-eyebrow">Great news</span>
					<h1>{this.formatEuro(this.state.previewPayoutCents)} available to claim</h1>
					<p>
						{this.normalisedRegistration()} · {this.formattedMileage()} km. We just need a few photos of your vehicle
						to complete your claim.
					</p>
				</div>
				<div className="c-taxi-claim__grow"/>
				<button type="button" className="c-taxi-claim__primary" onClick={() => this.openPhotoWizard()}>
					Continue {this.icon("arrow-right")}
				</button>
			</React.Fragment>
		);
	}

	private renderPhotos() {
		const definition = photoDefinitions[this.state.photoIndex];
		const photo = this.state.photos[definition.key];
		const busy = photo.status === "processing" || photo.status === "uploading";
		const lastPhoto = this.state.photoIndex === photoDefinitions.length - 1;
		return (
			<React.Fragment>
				{this.kicker(`Photo ${this.state.photoIndex + 1} of ${photoDefinitions.length}`)}
				<h1 className="c-taxi-claim__small-heading">{definition.label}</h1>
				<p className="c-taxi-claim__body-copy c-taxi-claim__body-copy--tight">
					{definition.description}
				</p>
				{definition.exampleUrl && (
					<button type="button" className="c-taxi-claim__example-link c-taxi-claim__example-link--standalone"
							onClick={() => this.openPhotoExample(definition.key)}>
						View example
					</button>
				)}
				<div className={`c-taxi-claim__photo-capture c-taxi-claim__photo-capture--${photo.status}`}>
					<div className="c-taxi-claim__photo-preview">
						{photo.previewUrl ? (
							<img src={photo.previewUrl} alt={`Captured ${definition.label.toLowerCase()}`}/>
						) : (
							<span>{this.icon(definition.icon.replace(/^fa-/, ""), 30)}</span>
						)}
						{busy && <span className="c-taxi-claim__photo-busy">{this.photoStatusText(photo.status)}</span>}
					</div>
					<span className="c-taxi-claim__photo-status" aria-live="polite">
						{this.photoStatusText(photo.status)}
					</span>
					{photo.error && <span className="c-taxi-claim__photo-error">{photo.error}</span>}
					<div className="c-taxi-claim__capture-actions">
						<input {...rearCameraCaptureProps} type="file" id={`taxi-photo-${definition.key}`} accept="image/*"
							   disabled={busy} onChange={(event) => this.selectPhoto(definition, event)}/>
						<label htmlFor={`taxi-photo-${definition.key}`} aria-disabled={busy}>
							{this.icon(photo.previewUrl ? "refresh" : "camera")}
							{photo.previewUrl ? "Retake photo" : "Take photo"}
						</label>
						{photo.status === "failed" && photo.file && (
							<button type="button" onClick={() => this.retryPhoto(definition)}>
								{this.icon("repeat", 14)} Retry upload
							</button>
						)}
					</div>
				</div>
				<div className="c-taxi-claim__grow"/>
				<button type="button" className="c-taxi-claim__primary"
						disabled={photo.status !== "done" || !photo.attachmentId}
						onClick={() => this.continuePhotoWizard()}>
					{lastPhoto ? "Review claim" : "Continue"} {this.icon("arrow-right")}
				</button>
			</React.Fragment>
		);
	}

	private renderReview() {
		return (
			<React.Fragment>
				{this.kicker("Review")}
				<h1 className="c-taxi-claim__small-heading">Review your claim</h1>
				<div className="c-taxi-claim__summary">
					{this.summaryRow("Registration", this.normalisedRegistration())}
					{this.summaryRow("Mileage", `${this.formattedMileage()} km`)}
					{this.summaryRow("NTA SPSV Licence Number", this.state.driverLicenceNumber.trim().toUpperCase())}
					{this.summaryRow("Photos", `${photoDefinitions.length} of ${photoDefinitions.length} added`)}
					{this.summaryRow("Claim amount", this.formatEuro(this.state.previewPayoutCents), true)}
				</div>
				<div className="c-taxi-claim__grow"/>
				<button type="button" className="c-taxi-claim__primary" onClick={() => this.navigate("signature")}>
					Continue to sign-off {this.icon("arrow-right")}
				</button>
			</React.Fragment>
		);
	}

	private renderSignature() {
		const utilityName = this.state.utilityName.trim();
		const waiverReady = !!utilityName;
		const alreadySubmitted = !!this.state.claimResult;
		return (
			<React.Fragment>
				{this.kicker("Final step")}
				<h1 className="c-taxi-claim__small-heading">Client Waiver Agreement</h1>
				<ol className="c-taxi-claim__waiver-list">
					<li>I hereby declare that the information provided in respect of this application is to the best of my knowledge correct. I am duly authorised to sign on behalf of the client organisation(s).</li>
					<li>I acknowledge that this transaction has been supported by the Energy Efficiency Obligation Scheme and that the associated energy credits have been assigned to {utilityName || "Utility name unavailable"}.</li>
					<li>I acknowledge that the vehicle purchaser will derive some benefit from our participation in the Energy Efficiency Obligation Scheme.</li>
					<li>Only battery electric vehicles which have been registered for the first time in Ireland are included in the application.</li>
				</ol>
				{!waiverReady && (
					<div className="c-taxi-claim__notice c-taxi-claim__notice--error" role="alert">
						We can't prepare the waiver because the utility name is unavailable. Please try again later or email {contactEmail}.
					</div>
				)}
				{waiverReady && !alreadySubmitted && (
					<SignaturePad ref={this.setSignaturePadRef} onStrokeChange={this.handleSignatureStrokeChange}/>
				)}
				{this.state.submissionError && (
					<div className="c-taxi-claim__notice c-taxi-claim__notice--error" role="alert">
						{this.state.submissionError}
					</div>
				)}
				<div className="c-taxi-claim__grow"/>
				<button type="button" className="c-taxi-claim__primary"
						disabled={!alreadySubmitted && (!waiverReady || !this.state.signatureHasStroke || this.state.submitting)}
						onClick={() => this.submitClaim()}>
					{alreadySubmitted
						? "Return to confirmation"
						: (this.state.submitting ? "Submitting claim…" : (this.state.submissionError ? "Try again" : "Sign & submit"))}
					<span style={this.state.submitting ? {display: "none"} : undefined}>
						{this.icon("arrow-right")}
					</span>
				</button>
			</React.Fragment>
		);
	}

	private renderConfirmation() {
		const result = this.state.claimResult;
		if (result && result.eligible) {
			return (
				<React.Fragment>
					<div className="c-taxi-claim__confirm-icon">{this.icon("check", 22)}</div>
					{this.kicker("Claim submitted")}
					<h1>You've earned {this.formatEuro(result.payoutCents)}</h1>
					<p className="c-taxi-claim__body-copy">
						Nice work — that's {this.formatEuro(result.payoutCents)} closer to the real cost of driving
						electric, and one more vehicle counted in Ireland's switch away from fossil fuels.
					</p>
					<p className="c-taxi-claim__body-copy c-taxi-claim__body-copy--tight">
						An email with your waiver and claim amount is on its way and should arrive within about two minutes.
						 Driving Green will then follow up for your bank details.
					</p>
					{this.claimReference(result.jobReference)}
					{this.confirmationContactPrompt()}
				</React.Fragment>
			);
		}
		return (
			<React.Fragment>
				<div className="c-taxi-claim__confirm-icon c-taxi-claim__confirm-icon--neutral">
					{this.icon("file-text", 20)}
				</div>
				{this.kicker("Claim received")}
				{this.claimIneligibleMessage()}
				{result && this.claimReference(result.jobReference)}
				{this.confirmationContactPrompt()}
			</React.Fragment>
		);
	}

	private renderIneligible() {
		if (this.state.ineligibleReason === "claim") {
			return (
				<React.Fragment>
					{this.kicker("Claim eligibility")}
					{this.claimIneligibleMessage()}
				</React.Fragment>
			);
		}
		const vehicleReason = this.state.ineligibleReason === "vehicle";
		return (
			<React.Fragment>
				{this.kicker(vehicleReason ? "Vehicle type" : "Ownership")}
				<h1>It doesn't look like you're eligible right now</h1>
				<p className="c-taxi-claim__body-copy">
					{vehicleReason
						? "This claim is for registered taxi and private-hire vehicles, so we can't process it today."
						: "Funding claims can only be submitted by the vehicle's registered owner, so we can't process it today."}
					{" "}Driving Green will still be in touch about other ways we can help.
				</p>
				{this.contactPrompt()}
			</React.Fragment>
		);
	}

	private renderExistingAccount() {
		return (
			<React.Fragment>
				<div className="c-taxi-claim__confirm-icon c-taxi-claim__confirm-icon--neutral">
					{this.icon("user", 22)}
				</div>
				{this.kicker("Account found")}
				<h1>It looks like you're already registered</h1>
				<p className="c-taxi-claim__body-copy">
					An account already exists for this email address or business. Contact Driving Green and someone will
					help you continue your claim.
				</p>
				{this.contactPrompt()}
			</React.Fragment>
		);
	}

	private choiceScreen(
		kicker: string,
		title: string,
		body: string,
		onYes: () => void,
		onNo: () => void,
		yesLabel: string = "Yes",
		noLabel: string = "No",
		yesIcon?: string
	) {
		return (
			<React.Fragment>
				{this.kicker(kicker)}
				<h1 className="c-taxi-claim__small-heading">{title}</h1>
				<p className="c-taxi-claim__body-copy c-taxi-claim__body-copy--tight">{body}</p>
				<div className="c-taxi-claim__choices">
					<button type="button" onClick={onYes}>
						{yesIcon && this.icon(yesIcon.replace(/^fa-/, ""))} {yesLabel}
					</button>
					<button type="button" onClick={onNo}>{noLabel}</button>
				</div>
			</React.Fragment>
		);
	}

	private kicker(text: string) {
		return <span className="c-taxi-claim__kicker"><span aria-hidden="true"/>{text}</span>;
	}

	/**
	 * Inline SVG icons, deliberately not the "fa fa-…" class + Font Awesome's JS kit used elsewhere on the
	 * site. That kit replaces <i> nodes with <svg> nodes outside React's control; when this wizard unmounts
	 * a screen while that replacement is still in flight, React's removeChild call races it and throws
	 * "not a child of this node". Inline SVG has nothing for the kit to find, so there is nothing to race.
	 */
	private icon(name: string, size: number = 16) {
		const strokeProps = {
			width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
			strokeWidth: 2, strokeLinecap: "round" as "round", strokeLinejoin: "round" as "round", "aria-hidden": true
		};
		switch (name) {
			case "chevron-left":
				return <svg {...strokeProps}><path d="M15 18l-6-6 6-6"/></svg>;
			case "arrow-right":
				return <svg {...strokeProps}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
			case "check":
				return <svg {...strokeProps}><path d="M5 13l4 4L19 7"/></svg>;
			case "file-text":
			case "file-text-o":
				return (
					<svg {...strokeProps}>
						<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
						<path d="M14 3v6h6"/>
						<path d="M9 13h6M9 17h6"/>
					</svg>
				);
			case "external-link":
				return (
					<svg {...strokeProps}>
						<path d="M14 5h5v5"/><path d="M19 5L10 14"/><path d="M18 13v6H5V6h6"/>
					</svg>
				);
			case "user":
				return <svg {...strokeProps}><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0 1 16 0v1"/></svg>;
			case "bolt":
				return (
					<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
						<path d="M13 2 4 14h6l-1 8 9-12h-6z"/>
					</svg>
				);
			case "id-card":
			case "id-card-o":
				return (
					<svg {...strokeProps}>
						<rect x="3" y="5" width="18" height="14" rx="2"/>
						<circle cx="8" cy="12" r="2"/>
						<path d="M13 10h6M13 14h4"/>
					</svg>
				);
			case "tachometer":
				return (
					<svg {...strokeProps}>
						<path d="M4 15a8 8 0 1 1 16 0"/><path d="M12 15l3.5-4.5"/><circle cx="12" cy="15" r="1"/>
					</svg>
				);
			case "car":
				return (
					<svg {...strokeProps}>
						<path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
						<circle cx="12" cy="13.5" r="3.5"/>
					</svg>
				);
			case "camera":
				return (
					<svg {...strokeProps}>
						<path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
						<circle cx="12" cy="13.5" r="3.5"/>
					</svg>
				);
			case "refresh":
				return <svg {...strokeProps}><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/></svg>;
			case "repeat":
				return <svg {...strokeProps}><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/></svg>;
			case "times":
				return <svg {...strokeProps}><path d="M18 6L6 18M6 6l12 12"/></svg>;
			default:
				return null;
		}
	}

	private textField(
		field: "firstName" | "lastName" | "businessName" | "email" | "phoneNumber" | "driverLicenceNumber",
		label: string,
		type: "text" | "email" | "tel",
		placeholder: string,
		maxLength: number,
		labelNote?: string
	) {
		return (
			<div className="c-taxi-claim__field">
				<label htmlFor={`taxi-claim-${field}`}>
					{label}
					{labelNote && <span className="c-taxi-claim__label-note">{labelNote}</span>}
				</label>
				<input id={`taxi-claim-${field}`} type={type} value={(this.state as any)[field]} maxLength={maxLength}
					   placeholder={placeholder} autoComplete={this.autoCompleteFor(field)}
					   onChange={(event) => this.updateAccountField(field, event.target.value)}/>
				{this.accountError(field)}
			</div>
		);
	}

	private autoCompleteFor(field: string): string {
		switch (field) {
			case "firstName": return "given-name";
			case "lastName": return "family-name";
			case "businessName": return "organization";
			case "email": return "email";
			case "phoneNumber": return "tel";
			default: return "off";
		}
	}

	private accountError(field: string) {
		return this.state.accountErrors[field]
			? <span className="c-taxi-claim__field-error">{this.state.accountErrors[field]}</span>
			: null;
	}

	private vehicleError(field: string) {
		return this.state.vehicleErrors[field]
			? <span className="c-taxi-claim__field-error">{this.state.vehicleErrors[field]}</span>
			: null;
	}

	private renderPhotoExample() {
		if (!this.state.examplePhotoKey) return null;
		const definition = photoDefinitions.filter(photo => photo.key === this.state.examplePhotoKey)[0];
		if (!definition || !definition.exampleUrl) return null;
		return (
			<div className="c-taxi-claim__example-backdrop" role="presentation"
				 onClick={() => this.closePhotoExample()}>
				<div className="c-taxi-claim__example-dialog" role="dialog" aria-modal="true"
					 aria-labelledby="taxi-claim-example-title" tabIndex={-1}
					 ref={(element) => this.exampleDialog = element}
					 onClick={(event) => event.stopPropagation()}
					 onKeyDown={(event) => {
						 if (event.key === "Escape") this.closePhotoExample();
					 }}>
					<button type="button" className="c-taxi-claim__example-close"
							onClick={() => this.closePhotoExample()} aria-label="Close example photo">
						{this.icon("times", 12)}
					</button>
					<span className="c-taxi-claim__example-eyebrow">Example photo</span>
					<h2 id="taxi-claim-example-title">{definition.label}</h2>
					<img src={definition.exampleUrl} alt={`A good example of ${definition.label.toLowerCase()}`}/>
					<p>{definition.description}</p>
				</div>
			</div>
		);
	}

	private openPhotoExample(examplePhotoKey: PhotoKey) {
		this.setState({examplePhotoKey}, () => {
			if (this.exampleDialog) this.exampleDialog.focus();
		});
	}

	private closePhotoExample() {
		this.setState({examplePhotoKey: ""});
	}

	private photoStatusText(status: PhotoStatus): string {
		switch (status) {
			case "processing": return "Preparing photo…";
			case "uploading": return "Uploading…";
			case "done": return "Uploaded";
			case "failed": return "Upload failed";
			default: return "Photo required";
		}
	}

	private summaryRow(label: string, value: string, total: boolean = false) {
		return (
			<div className={total ? "c-taxi-claim__summary-row--total" : undefined}>
				<span>{label}</span><strong>{value}</strong>
			</div>
		);
	}

	/**
	 * The one thing the driver is asked to quote back, so it is the one thing they can take with them:
	 * shown at a size worth reading and copied with a tap, rather than retyped from a phone screen into
	 * an email.
	 */
	private claimReference(reference: string) {
		return reference ? (
			<div className="c-taxi-claim__reference">
				<span className="c-taxi-claim__reference-label">Claim reference</span>
				<div className="c-taxi-claim__reference-row">
					<strong ref={(element) => this.referenceValue = element}>{reference}</strong>
					<button type="button"
							className="c-taxi-claim__reference-copy"
							onClick={() => this.copyReference(reference)}>
						{this.state.referenceCopied ? "Copied" : "Copy"}
					</button>
				</div>
			</div>
		) : null;
	}

	private copyReference(reference: string) {
		// Selected first, because that always works and is visible. The clipboard is attempted on top of
		// it: plenty of browsers and privacy settings refuse it outright, and a button that silently does
		// nothing is worse than no button. When it is refused the driver still has the reference
		// highlighted and one tap from their own copy menu.
		this.selectReference();
		try {
			navigator.clipboard.writeText(reference).then(
					() => this.setState({referenceCopied: true}),
					() => undefined);
		} catch (ignored) {
			// nothing to do - the reference is selected and readable either way
		}
	}

	private selectReference() {
		if (!this.referenceValue || !window.getSelection || !document.createRange) {
			return;
		}
		const range = document.createRange();
		range.selectNodeContents(this.referenceValue);
		const selection = window.getSelection();
		selection.removeAllRanges();
		selection.addRange(range);
	}

	private claimIneligibleMessage() {
		return (
			<React.Fragment>
				<h1>It doesn't look like you're eligible right now.</h1>
				<p className="c-taxi-claim__body-copy">
					This may be because your vehicle is less than 30 days old, not electric or already claimed by another
					owner. If you'd like to find out more about this or other services Driving Green can offer, please
					contact us on <a href={`mailto:${contactEmail}`}>{contactEmail}</a> and we'll be happy to help.
				</p>
			</React.Fragment>
		);
	}

	private contactPrompt() {
		return (
			<p className="c-taxi-claim__contact">
				Need help? Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
			</p>
		);
	}

	private confirmationContactPrompt() {
		return (
			<p className="c-taxi-claim__body-copy">
				If you need help, contact us at <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
				{" "}Please include your claim reference number with any queries.
			</p>
		);
	}

	private navigate(step: JourneyStep) {
		this.setState({step, stepHistory: this.state.stepHistory.concat([this.state.step])}, () => {
			if (this.screen) this.screen.focus();
		});
	}

	/**
	 * Moves on and drops everything behind it, which is what hides the back button - there is no
	 * separate rule for that, an empty history is the rule.
	 * <p>
	 * Only two places earn it: the sign-up form once the account exists, which would answer
	 * nothing but "you already have an account", and the submitted claim. Everywhere the journey
	 * stops short - not an EV, not a taxi, not the owner, already registered - deliberately keeps
	 * its history, because those are dead ends a driver reaches by answering one question wrong
	 * and must be able to back out of.
	 */
	private navigateFresh(step: JourneyStep) {
		this.setState({step, stepHistory: []}, () => {
			if (this.screen) this.screen.focus();
		});
	}

	private openPhotoWizard() {
		const firstIncomplete = photoDefinitions.findIndex(definition => {
			const photo = this.state.photos[definition.key];
			return photo.status !== "done" || !photo.attachmentId;
		});
		this.setState({photoIndex: firstIncomplete === -1 ? photoDefinitions.length - 1 : firstIncomplete}, () => {
			this.navigate("photos");
		});
	}

	private continuePhotoWizard() {
		if (this.state.photoIndex < photoDefinitions.length - 1) {
			this.setState({photoIndex: this.state.photoIndex + 1}, () => {
				if (this.screen) this.screen.focus();
			});
			return;
		}
		if (this.photoStepComplete()) this.navigate("review");
	}

	private finishAt(step: JourneyStep) {
		this.setState({uploadStarted: false}, () => this.navigate(step));
	}

	private goBack() {
		if (this.state.step === "photos" && this.state.photoIndex > 0) {
			this.setState({photoIndex: this.state.photoIndex - 1}, () => {
				if (this.screen) this.screen.focus();
			});
			return;
		}
		if (!this.state.stepHistory.length) return;
		const history = this.state.stepHistory.slice();
		const step = history.pop();
		this.setState({step, stepHistory: history, submissionError: ""}, () => {
			if (this.screen) this.screen.focus();
		});
	}

	private canGoBack(): boolean {
		if (this.state.accountLoading || this.state.previewLoading || this.state.submitting) return false;
		return (this.state.step === "photos" && this.state.photoIndex > 0) || this.state.stepHistory.length > 0;
	}

	private updateAccountField(field: string, value: string) {
		const accountErrors = {...this.state.accountErrors};
		delete accountErrors[field];
		const update: any = {accountErrors, accountRequestError: ""};
		update[field] = value;
		this.setState(update);
	}

	private selectAddress(address: Address) {
		const accountErrors = {...this.state.accountErrors};
		delete accountErrors.address;
		this.setState({address: option(address), accountErrors, accountRequestError: ""});
	}

	private clearAddress() {
		this.setState({address: none}, () => {
			if (this.addressSelection) this.addressSelection.resetSearchInput();
		});
	}

	private toggleManualAddress(manualAddress: boolean) {
		const accountErrors = {...this.state.accountErrors};
		delete accountErrors.address;
		this.setState({
			manualAddress, address: none,
			manualStreet: "", manualCity: "", manualEircode: "",
			accountErrors, accountRequestError: ""
		});
	}

	/**
	 * City is required. Street and Eircode are optional so rural addresses can be submitted.
	 */
	private updateManualAddress(field: "manualStreet" | "manualCity" | "manualEircode", value: string) {
		const next = {...this.state, [field]: value};
		const city = next.manualCity.trim();
		const eircode = next.manualEircode.trim();
		const street = next.manualStreet.trim();
		// The Eircode no longer gates this: plenty of Irish addresses have none, and without the
		// town there is nothing to build an address from anyway.
		const address = city
			? option(Address.fromManualInput(none, none, none, street ? option(street) : none, option(city), eircode))
			: none;
		const accountErrors = {...this.state.accountErrors};
		delete accountErrors.address;
		this.setState({[field]: value, address, accountErrors, accountRequestError: ""} as any);
	}

	private submitAccount(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (this.state.accountLoading) return;
		const accountErrors = this.validateAccount();
		if (Object.keys(accountErrors).length) {
			this.setState({accountErrors, accountRequestError: ""});
			return;
		}
		this.setState({accountLoading: true, accountErrors: {}, accountRequestError: ""});
		this.signupApi.createAccount(this.accountApiData()).then(response => {
			if (!response.accessToken) {
				this.accessToken = "";
				this.setState({accountLoading: false}, () => this.finishAt("existing-account"));
				return;
			}
			this.accessToken = response.accessToken;
			this.setState({
				accountLoading: false,
				utilityName: (response.utilityName || "").trim()
		}, () => this.navigateFresh("taxi-check"));
		}).catch(error => {
			const exception = error && error.response && error.response.data && error.response.data.exception;
			// Only the email says somebody already has an account. A taken business name used to send
			// them here too, which turned every second sole trader called Joe Doe away from a journey
			// they had every right to finish. The back end now registers them under a numbered name
			// instead, and the rare collision it cannot resolve falls through to a message they can act
			// on rather than to a dead end.
			if (exception === "EmailExistsException") {
				this.accessToken = "";
				this.setState({accountLoading: false}, () => this.finishAt("existing-account"));
				return;
			}
			if (this.turnstileWidget) this.turnstileWidget.reset();
			this.setState({
				accountLoading: false,
				turnstileToken: "",
				accountRequestError: this.signupErrorMessage(error)
			});
		});
	}

	private validateAccount(): {[field: string]: string} {
		const errors: {[field: string]: string} = {};
		if (!this.state.firstName.trim()) errors.firstName = "First name is required";
		if (!this.state.lastName.trim()) errors.lastName = "Last name is required";
		if (!this.state.businessName.trim()) errors.businessName = "Business name is required";
		if (!this.state.email.trim()) errors.email = "Email address is required";
		else if (!validator.isEmail(this.state.email.trim())) errors.email = "Enter a valid email address";
		if (!this.state.phoneNumber.trim()) errors.phoneNumber = "Phone number is required";
		if (!this.state.driverLicenceNumber.trim()) errors.driverLicenceNumber = "NTA SPSV Licence Number is required";
		if (this.state.address.isEmpty) errors.address = "Select your business address";
		else {
			const address = this.state.address.get;
			if (address.city.isEmpty) {
				errors.address = "Select an address with a city or town";
			}
		}
		if (!this.state.turnstileToken) errors.turnstileToken = "Complete the security check";
		if (!this.state.consent) errors.consent = "Consent is required to continue";
		return errors;
	}

	private accountApiData(): SelfServiceSignupApiDto {
		return {
			firstName: this.state.firstName.trim(),
			lastName: this.state.lastName.trim(),
			businessName: this.state.businessName.trim(),
			email: this.state.email.trim(),
			phoneNumber: this.state.phoneNumber.trim(),
			driverLicenceNumber: this.state.driverLicenceNumber.trim(),
			address: this.addressApiData(this.state.address.get),
			turnstileToken: this.state.turnstileToken,
			jobTypes: ["SERVICE"],
			sendSetPasswordEmail: false,
			source: journeySource
		};
	}

	private addressApiData(address: Address): SelfServiceSignupAddressApiDto {
		return {
			street: address.street.orNull,
			city: address.city.get,
			eircode: (address.postcode || "").trim(),
			buildingName: address.buildingName.orNull,
			buildingNumber: address.buildingNumber.orNull,
			subBuilding: address.subBuilding.orNull,
			secStreet: address.secondaryStreet.orNull,
			district: address.district.orNull,
			province: address.province.orNull
		};
	}

	private signupErrorMessage(error: any): string {
		const response = error && error.response;
		const status = response && response.status;
		const exception = response && response.data && response.data.exception;
		const message = response && response.data && response.data.message;
		if (exception === "BotCheckFailedException") {
			return message === "Bot check is unavailable"
				? "The security check is temporarily unavailable. Please try again later."
				: "The security check was not accepted. Please complete it again.";
		}
		if (exception === "TooManyRequestsException" || status === 429) {
			return "There have been too many attempts. Please wait a few minutes and try again.";
		}
		if (exception === "SignupNotConfiguredException" || status === 503) {
			return `Registration is temporarily unavailable. Please email ${contactEmail} for help.`;
		}
		if (status === 400) return "Please check the information in the form and try again.";
		return `We could not create your account. Please try again or email ${contactEmail} for help.`;
	}

	private updateVehicleField(field: "registration" | "mileage", value: string) {
		const vehicleErrors = {...this.state.vehicleErrors};
		delete vehicleErrors[field];
		const update: any = {vehicleErrors, previewError: "", previewPayoutCents: 0};
		update[field] = value;
		this.setState(update);
	}

	private submitVehicle(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (this.state.previewLoading) return;
		const vehicleErrors: {[field: string]: string} = {};
		if (!this.normalisedRegistration()) vehicleErrors.registration = "Registration is required";
		const mileage = this.numericMileage();
		if (!this.state.mileage.trim()) vehicleErrors.mileage = "Current mileage is required";
		else if (isNaN(mileage) || mileage < 0) vehicleErrors.mileage = "Enter the current mileage using numbers only";
		if (Object.keys(vehicleErrors).length) {
			this.setState({vehicleErrors});
			return;
		}
		if (!this.accessToken) {
			this.setState({
				previewError: `Your claim session is no longer available. Email ${contactEmail} for help.`
			});
			return;
		}
		this.setState({vehicleErrors: {}, previewLoading: true, previewError: ""});
		this.claimApi.previewClaim({
			registration: this.normalisedRegistration(),
			mileage,
			// Sent here as well as with the claim: the amount quoted on the next screen is worked out from
			// the journey, so leaving it off would quote what somebody else would be paid.
			source: journeySource
		}, this.accessToken).then(preview => {
			if (!preview.eligible) {
				this.setState({previewLoading: false, ineligibleReason: "claim"}, () => this.finishAt("ineligible"));
				return;
			}
			this.setState({
				previewLoading: false,
				previewError: "",
				previewPayoutCents: preview.payoutCents
			}, () => this.navigate("eligible"));
		}).catch(() => {
			this.setState({
				previewLoading: false,
				previewError: "We couldn't check this vehicle right now. Check your connection and try again."
			});
		});
	}

	private selectPhoto(definition: PhotoDefinition, event: React.ChangeEvent<HTMLInputElement>) {
		const file = event.target.files && event.target.files[0];
		event.target.value = "";
		if (!file) return;
		const currentPreviewUrl = this.state.photos[definition.key].previewUrl;
		if (currentPreviewUrl) URL.revokeObjectURL(currentPreviewUrl);
		this.updatePhoto(definition.key, {
			status: "processing",
			file,
			blob: null,
			previewUrl: "",
			attachmentId: "",
			error: ""
		}, true, () => this.prepareAndUploadPhoto(definition, file));
	}

	private retryPhoto(definition: PhotoDefinition) {
		const photo = this.state.photos[definition.key];
		if (!photo.file) return;
		if (photo.blob) {
			this.uploadPreparedPhoto(definition, photo.blob);
		} else {
			this.updatePhoto(definition.key, {status: "processing", error: ""}, true,
				() => this.prepareAndUploadPhoto(definition, photo.file));
		}
	}

	private prepareAndUploadPhoto(definition: PhotoDefinition, file: File) {
		reencodeImageToJpeg(file).then(blob => {
			const previewUrl = URL.createObjectURL(blob);
			this.updatePhoto(definition.key, {blob, previewUrl, status: "uploading", error: ""}, true,
				() => this.uploadPreparedPhoto(definition, blob));
		}).catch(() => {
			this.updatePhoto(definition.key, {
				status: "failed",
				error: "This photo could not be read. Choose another image or try taking it again."
			}, true);
		});
	}

	private uploadPreparedPhoto(definition: PhotoDefinition, blob: Blob) {
		if (!this.accessToken) {
			this.updatePhoto(definition.key, {
				status: "failed",
				error: `Your claim session is no longer available. Email ${contactEmail} for help.`
			}, true);
			return;
		}
		this.updatePhoto(definition.key, {status: "uploading", error: ""}, true);
		const name = attachmentFileName(definition.prefix, this.normalisedRegistration(), "jpg");
		this.claimApi.uploadAttachment(blob, name, this.accessToken).then(attachmentId => {
			this.updatePhoto(definition.key, {status: "done", attachmentId, error: ""}, true);
		}).catch(() => {
			this.updatePhoto(definition.key, {
				status: "failed",
				error: "We couldn't upload this photo. Check your connection and retry just this image."
			}, true);
		});
	}

	private updatePhoto(
		key: PhotoKey,
		update: Partial<PhotoUploadState>,
		uploadStarted: boolean,
		callback?: () => void
	) {
		const photos = {...this.state.photos};
		photos[key] = {...photos[key], ...update};
		this.setState({photos, uploadStarted}, callback);
	}

	private photoStepComplete(): boolean {
		return photoDefinitions.every(definition => {
			const photo = this.state.photos[definition.key];
			return photo.status === "done" && !!photo.attachmentId;
		});
	}

	private submitClaim() {
		if (this.state.claimResult) {
			this.setState({step: "confirmation", stepHistory: [], submissionError: ""}, () => {
				if (this.screen) this.screen.focus();
			});
			return;
		}
		if (this.submitLocked || this.state.submitting || !this.state.signatureHasStroke || !this.signaturePad) return;
		if (!this.state.utilityName.trim()) {
			this.setState({submissionError: "The utility name is required before this waiver can be signed."});
			return;
		}
		if (!this.accessToken) {
			this.setState({submissionError: `Your claim session is no longer available. Email ${contactEmail} for help.`});
			return;
		}
		this.submitLocked = true;
		this.setState({submitting: true, submissionError: ""});
		const signatureId = this.state.signatureAttachmentId
			? Promise.resolve(this.state.signatureAttachmentId)
			: this.signaturePad.toBlob().then(blob => {
				const name = attachmentFileName("signature_", this.normalisedRegistration(), "png");
				return this.claimApi.uploadAttachment(blob, name, this.accessToken);
			});

		signatureId.then(attachmentId => {
			this.setState({signatureAttachmentId: attachmentId});
			return this.claimApi.submitClaim(this.claimApiData(attachmentId), this.accessToken);
		}).then(claimResult => {
			this.accessToken = "";
			this.submitLocked = false;
			this.setState({
				claimResult,
				step: "confirmation",
				stepHistory: [],
				uploadStarted: false,
				submitting: false,
				submissionError: ""
			}, () => {
				if (this.screen) this.screen.focus();
			});
		}).catch(() => {
			this.submitLocked = false;
			this.setState({
				submitting: false,
				submissionError: "We couldn't submit the claim. Check your connection and try again — your uploaded photos are already saved."
			});
		});
	}

	private claimApiData(signatureAttachmentId: string): TaxiClaimSubmissionApiDto {
		return {
			registration: this.normalisedRegistration(),
			mileage: this.numericMileage(),
			attachmentIds: photoDefinitions.map(definition => this.state.photos[definition.key].attachmentId || ""),
			signatureAttachmentId,
			source: journeySource
		};
	}

	private warnBeforeUnload(event: BeforeUnloadEvent) {
		if (!this.state.uploadStarted || this.state.step === "confirmation") return;
		event.preventDefault();
		event.returnValue = "Your uploaded claim progress will be lost if you leave this page.";
		return event.returnValue;
	}

	/**
	 * Matches IrishVehicleRegistrationValidator.normalize on the backend exactly (strip whitespace and
	 * hyphens, uppercase), so the value we submit, key attachments by, and show back to the driver is the
	 * same string the backend will look the vehicle up by — not a lookalike that only differs in spacing.
	 */
	private normalisedRegistration(): string {
		return this.state.registration.replace(/[\s-]/g, "").toUpperCase();
	}

	private numericMileage(): number {
		const value = this.state.mileage.replace(/[\s,]/g, "");
		return /^\d+$/.test(value) ? parseInt(value, 10) : NaN;
	}

	private formattedMileage(): string {
		const mileage = this.numericMileage();
		return isNaN(mileage) ? this.state.mileage : mileage.toLocaleString("en-IE");
	}

	private formatEuro(cents: number): string {
		const euro = cents / 100;
		return `€${euro.toLocaleString("en-IE", {
			minimumFractionDigits: cents % 100 ? 2 : 0,
			maximumFractionDigits: 2
		})}`;
	}
}

function isProduction(environment: string): boolean {
	return environment === Environment.Prod || environment === Environment.Production;
}

function mapStateToProps(state: RootState): ReduxProps {
	return {
		turnstileSiteKey: state.settings.settings.map(settings => settings.turnstileSiteKey).getOrElse(""),
		// Both conditions, not either. The measurement id says analytics is configured at all; the
		// environment says this is the only place allowed to report into the client's property, so a
		// value copied into a lower environment's configuration cannot quietly mix test traffic in.
		//
		// Both spellings, because production answers "prod" - read off /settings on the live host -
		// while the enum's other member matches the build-time NODE_ENV. Accepting only one of them
		// would have left analytics switched off on the one environment it is meant for, saying
		// nothing about it.
		analyticsEnabled: state.settings.settings
			.map(settings => isProduction(settings.environment) && !!settings.gaMeasurementId)
			.getOrElse(false),
		gaMeasurementId: state.settings.settings.map(settings => settings.gaMeasurementId).getOrElse("")
	};
}

export default connect(mapStateToProps)(TaxiClaimPage);
